import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, RefreshCw, Trash2 } from "lucide-react";
import { LOGO_URL } from "../lib/data";
import { TID } from "../lib/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ["new", "reviewed", "archived"];

const StatusPill = ({ value }) => {
  const colors = {
    new: "bg-[#00F0FF]/15 text-[#00F0FF] border-[#00F0FF]/30",
    reviewed: "bg-white/10 text-white border-white/20",
    archived: "bg-white/5 text-white/50 border-white/10",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[0.65rem] font-mono-em tracking-[0.16em] uppercase border ${colors[value]}`}
    >
      {value}
    </span>
  );
};

const SourcePill = ({ value }) => {
  const s = value || "contact";
  const styles =
    s === "sponsorship"
      ? "bg-[#FF007F]/15 text-[#FF007F] border-[#FF007F]/30"
      : "bg-white/[0.04] text-white/70 border-white/15";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[0.6rem] font-mono-em tracking-[0.16em] uppercase border ${styles}`}>
      {s}
    </span>
  );
};

export default function Admin() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, reviewed: 0, archived: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        axios.get(`${API}/inquiries`),
        axios.get(`${API}/inquiries/stats`),
      ]);
      setItems(list);
      setStats(s);
      setSelected((prev) => (prev ? list.find((x) => x.id === prev.id) || null : null));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/inquiries/${id}`, { status });
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await axios.delete(`${API}/inquiries/${id}`);
      setSelected(null);
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-page">
      <header className="sticky top-0 z-40 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors duration-400">
              <ArrowLeft size={16} />
              <span className="font-mono-em text-[0.7rem] tracking-[0.22em] uppercase">Back to site</span>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-8 w-8 object-contain" />
            <div className="font-display font-bold text-sm tracking-tight">EVENT MONK / ADMIN</div>
          </div>
          <button
            data-testid={TID.adminRefresh}
            onClick={load}
            className="em-btn em-btn-ghost border-white/15 text-xs"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-6 md:px-10 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-end justify-between gap-6 mb-10"
        >
          <div>
            <span className="em-tag">Inquiries dashboard</span>
            <h1 className="mt-4 font-display font-extrabold text-4xl md:text-5xl tracking-tighter leading-none">
              Studio Inbox
            </h1>
          </div>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { key: "total", label: "Total", tid: TID.adminStatTotal },
            { key: "new", label: "New", tid: TID.adminStatNew },
            { key: "reviewed", label: "Reviewed", tid: TID.adminStatReviewed },
            { key: "archived", label: "Archived", tid: TID.adminStatArchived },
          ].map((c) => (
            <div
              key={c.key}
              data-testid={c.tid}
              className="em-card p-5"
            >
              <div className="font-mono-em text-[0.65rem] tracking-[0.24em] uppercase text-white/50">
                {c.label}
              </div>
              <div className="mt-3 font-display font-extrabold text-3xl md:text-4xl tracking-tighter">
                {stats[c.key]}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-7">
            <div className="em-card overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-3 border-b border-white/10 font-mono-em text-[0.62rem] tracking-[0.22em] uppercase text-white/50">
                <div className="col-span-4">Name</div>
                <div className="col-span-4">Service</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2 text-right">Date</div>
              </div>
              <div data-testid={TID.adminTable} className="max-h-[560px] overflow-y-auto">
                {items.length === 0 && (
                  <div className="px-4 py-10 text-center text-white/50 text-sm">
                    {loading ? "Loading…" : "No inquiries yet. Submit the contact form to see one here."}
                  </div>
                )}
                {items.map((it) => (
                  <button
                    key={it.id}
                    onClick={() => setSelected(it)}
                    data-testid={`admin-row-${it.id}`}
                    className={`w-full text-left grid grid-cols-12 px-4 py-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors duration-300 ${
                      selected?.id === it.id ? "bg-white/[0.05]" : ""
                    }`}
                  >
                    <div className="col-span-4 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-medium truncate">{it.name}</div>
                        <SourcePill value={it.source} />
                      </div>
                      <div className="font-mono-em text-[0.62rem] text-white/50 truncate mt-0.5">{it.email}</div>
                    </div>
                    <div className="col-span-4 text-sm text-white/70 truncate">{it.service || "—"}</div>
                    <div className="col-span-2"><StatusPill value={it.status} /></div>
                    <div className="col-span-2 text-right font-mono-em text-[0.65rem] text-white/50">
                      {new Date(it.created_at).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <div className="em-card p-6 min-h-[400px] sticky top-24">
              {!selected ? (
                <div className="h-full grid place-items-center text-white/50 text-sm py-20">
                  Select an inquiry to view details.
                </div>
              ) : (
                <div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="font-display font-bold text-2xl tracking-tight">{selected.name}</div>
                        <SourcePill value={selected.source} />
                      </div>
                      <a href={`mailto:${selected.email}`} className="text-white/70 text-sm em-underline-link">
                        {selected.email}
                      </a>
                    </div>
                    <StatusPill value={selected.status} />
                  </div>

                  <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="font-mono-em text-[0.62rem] tracking-[0.22em] uppercase text-white/50">Company</div>
                      <div className="mt-1 text-white/90">{selected.company || "—"}</div>
                    </div>
                    <div>
                      <div className="font-mono-em text-[0.62rem] tracking-[0.22em] uppercase text-white/50">Service</div>
                      <div className="mt-1 text-white/90">{selected.service || "—"}</div>
                    </div>
                    <div>
                      <div className="font-mono-em text-[0.62rem] tracking-[0.22em] uppercase text-white/50">Budget</div>
                      <div className="mt-1 text-white/90">{selected.budget || "—"}</div>
                    </div>
                    <div>
                      <div className="font-mono-em text-[0.62rem] tracking-[0.22em] uppercase text-white/50">Received</div>
                      <div className="mt-1 font-mono-em text-[0.75rem] text-white/80">
                        {new Date(selected.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="font-mono-em text-[0.62rem] tracking-[0.22em] uppercase text-white/50">
                      Brief
                    </div>
                    <p className="mt-2 text-white/85 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
                  </div>

                  <div className="mt-8 flex flex-wrap items-center gap-2">
                    {STATUS_OPTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatus(selected.id, s)}
                        data-testid={`admin-set-status-${s}`}
                        className={`px-3 py-1.5 rounded-full border text-xs font-mono-em tracking-[0.16em] uppercase transition-[background-color,border-color] duration-400 ${
                          selected.status === s
                            ? "bg-white text-black border-white"
                            : "text-white/70 border-white/15 hover:border-white/40"
                        }`}
                      >
                        Mark {s}
                      </button>
                    ))}
                    <button
                      onClick={() => remove(selected.id)}
                      data-testid="admin-delete-button"
                      className="ml-auto inline-flex items-center gap-2 text-[#FF4D66] text-xs font-mono-em tracking-[0.16em] uppercase hover:opacity-80 transition-opacity duration-300"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
